import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import _ from 'lodash';
import moment from 'moment';
import { AddIcon, EditIcon } from '../../../../components/Icon';
import SearchInput from '../../../../components/SearchInput';
import StyleButton from '../../../../components/StyleButton';
import TableUI from '../../../../components/TableUI';
import { i18n } from '../../../../language';
import { systemAPI } from '../../../../apis';
import useUpdateEffect from '../../../../utils/useUpdateEffect';
import AddHospital from './Popup/AddHospital';
import EditHospital from './Popup/EditHospital';

const useStyle = makeStyles((theme) => ({
  total: {
    fontSize: '1rem',
    color: '#7d86a9',
    marginBottom: '0.875rem',
  },
  tableContent: {
    fontSize: '0.875rem',
    color: '#2e4469',
    lineHeight: '1.875rem',
  },
  editBtn: {
    width: '2.25rem',
    height: '2.25rem',
    minWidth: '2.25rem',
    minHeight: '2.25rem',
    backgroundColor: '#538fff',
    borderRadius: '10px',
  },
  editIcon: {
    fontSize: '1rem',
    color: '#fff',
  },
}));

const Hospital = () => {
  const classes = useStyle();
  const [addHospital, setAddHospital] = useState(false);
  const [editHospital, setEditHospital] = useState(false);
  const [hospitalList, setHospitalList] = useState({ count: 0, data: [] });
  const [refresh, setRefresh] = useState(false);
  const [pageInfo, setPageInfo] = useState({
    pageNo: 1,
    pageSize: 50,
    keyword: '',
  });

  const getHospitalList = useCallback(async () => {
    try {
      const { result } = await systemAPI.getSysHospitalList(pageInfo);

      if (pageInfo.pageNo > 1) {
        setHospitalList((oldData) => ({
          count: result.count,
          data: [...oldData.data, ...result.data],
        }));
      } else {
        setHospitalList(result);
      }
    } catch (error) {
      console.log(error);
    }
  }, [pageInfo]);

  useEffect(() => {
    (async () => {
      await getHospitalList();
    })();
  }, [getHospitalList]);

  useUpdateEffect(() => {
    setPageInfo((oldInfo) => ({ ...oldInfo, pageNo: 1 }));
  }, [refresh]);

  const addHospitalHandler = useCallback(
    (open) => () => {
      setAddHospital(open);
    },
    []
  );

  const editHospitalHandler = useCallback(
    (props) => () => {
      setEditHospital(props);
    },
    []
  );

  const tableData = useMemo(() => {
    return _.map(hospitalList.data, (item) => ({
      ...item,
      hospitalName: item.hospitalName,
      hospitalCode: item.hospitalCode,
      lastUpdate: item.lastUpdate ? moment(item.lastUpdate).format('yyyy-MM-DD HH:mm:ss') : '',
    }));
  }, [hospitalList.data]);

  const handleRefresh = useCallback(() => {
    setRefresh((o) => !o);
    setAddHospital(false);
    setEditHospital(false);
  }, []);

  const turnPage = useCallback(() => {
    if (hospitalList.data.length < hospitalList.count) {
      setPageInfo((oldInfo) => ({
        ...oldInfo,
        pageNo: oldInfo.pageNo + 1,
      }));
    }
  }, [hospitalList]);

  useEffect(() => {
    const pageRef = document.getElementById('tab-content');
    if (pageRef) {
      const handleScroll = (event) => {
        const { scrollTop, clientHeight, scrollHeight } = pageRef;

        if (scrollTop + clientHeight + 1 >= scrollHeight) {
          turnPage();
        }
      };

      pageRef.addEventListener('scroll', handleScroll);
      return () => pageRef.removeEventListener('scroll', handleScroll);
    }
  }, [turnPage]);

  const handleSearchInput = useCallback((value) => {
    setPageInfo((oldData) => ({
      ...oldData,
      keyword: value,
      pageNo: 1,
    }));
  }, []);

  return (
    <>
      <Box display="flex" p="1.875rem" borderBottom="solid 1px #ddd" justifyContent="space-between">
        <SearchInput
          width="13.188rem"
          height="2.563rem"
          placeholder={i18n.t('SETTING_SEARCH_HOSPITAL_PLACEHOLDER')}
          onChange={handleSearchInput}
        />
        <StyleButton
          width="11.625rem"
          type="fill"
          text="Add Hospital"
          Icon={AddIcon}
          onClick={addHospitalHandler(true)}
        />
      </Box>
      <Box display="flex" flexDirection="column" p="1.25rem 1.875rem">
        <div className={classes.total}>{i18n.t('SETTING_RESULT', { length: hospitalList.count })}</div>
        <TableUI turnPages={turnPage}>
          <TableHead>
            <TableRow>
              <TableCell align="left" style={{ width: '30%' }}>
                {i18n.t('SETTING_HOSPITAL_TABLE_NAME')}
              </TableCell>
              <TableCell align="left" style={{ width: '30%' }}>
                {i18n.t('SETTING_HOSPITAL_TABLE_CODE')}
              </TableCell>
              <TableCell align="left" style={{ width: '20%' }}>
                {i18n.t('SETTING_HOSPITAL_TABLE_LAST_UPDATE')}
              </TableCell>
              <TableCell align="left" style={{ width: '5%' }} />
            </TableRow>
          </TableHead>

          <TableBody>
            {tableData.map((item) => (
              <TableRow onDoubleClick={editHospitalHandler(item)} key={item.hospitalId}>
                <TableCell>
                  <div className={classes.tableContent}> {item.hospitalName}</div>
                </TableCell>
                <TableCell>
                  <div className={classes.tableContent}> {item.hospitalCode}</div>
                </TableCell>
                <TableCell>
                  <div className={classes.tableContent}> {item.lastUpdate}</div>
                </TableCell>
                <TableCell>
                  <ButtonBase className={classes.editBtn} onClick={editHospitalHandler(item)}>
                    <EditIcon className={classes.editIcon} />
                  </ButtonBase>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </TableUI>
      </Box>
      {addHospital && <AddHospital handleClose={addHospitalHandler(false)} handleRefresh={handleRefresh} />}
      {editHospital ? (
        <EditHospital handleClose={editHospitalHandler(false)} handleRefresh={handleRefresh} data={editHospital} />
      ) : (
        ''
      )}
    </>
  );
};

export default Hospital;
